import { useEffect } from 'react';
import { CheckCircle, AlertCircle, X } from 'lucide-react';
import useBodyScrollLock from '../../hooks/useBodyScrollLock';

export default function VerificationResultDrawer({
  isOpen,
  isSuccess,
  onClose,
  onProceed,
}) {
  // Lock body scroll when drawer is open
  useBodyScrollLock(isOpen);

  // Close on escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  return (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/50 z-[90] animate-fade-in"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-[95] flex items-center justify-center p-4 animate-scale-in">
        <div className="bg-white rounded-3xl shadow-2xl max-w-md w-full relative">
          {/* Close Button */}
          <button
            onClick={onClose}
            className="absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full transition-colors z-10"
          >
            <X size={20} />
          </button>

          {/* Content */}
          <div className="px-6 py-8">
            {/* Icon */}
            <div className="flex justify-center mb-6">
              {isSuccess ? (
                <div className="w-20 h-20 bg-emerald-100 rounded-full flex items-center justify-center">
                  <CheckCircle size={48} className="text-emerald-500" />
                </div>
              ) : (
                <div className="w-20 h-20 bg-red-100 rounded-full flex items-center justify-center">
                  <AlertCircle size={48} className="text-red-500" />
                </div>
              )}
            </div>

            {/* Message */}
            <div className="text-center mb-8">
              <h2 className="text-xl font-bold text-gray-900 mb-2">
                {isSuccess ? 'Identity Successfully Verified' : 'Verification Failed'}
              </h2>
              <p className="text-gray-600">
                {isSuccess
                  ? 'You can now save more with your groups on Bonndle!'
                  : 'Check the details provided and try again.'}
              </p>
            </div>

            {/* Action Button */}
            <button
              onClick={onProceed}
              className={`w-full py-4 rounded-2xl font-bold text-lg transition-all active:scale-[0.98] ${
                isSuccess
                  ? 'bg-indigo-600 text-white hover:bg-indigo-700'
                  : 'bg-gray-900 text-white hover:bg-gray-800'
              }`}
            >
              {isSuccess ? 'Proceed' : 'Try Again'}
            </button>
          </div>
        </div>
      </div>

      {/* Animations */}
      <style>{`
        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        @keyframes scaleIn {
          from {
            transform: scale(0.9);
            opacity: 0;
          }
          to {
            transform: scale(1);
            opacity: 1;
          }
        }

        .animate-fade-in {
          animation: fadeIn 0.2s ease-out;
        }

        .animate-scale-in {
          animation: scaleIn 0.2s ease-out;
        }
      `}</style>
    </>
  );
}
